import type { FunctionComponent, ReactNode } from "react";
import { graphql, useFragment } from "react-relay";

import { section as sectionData } from "shared/data";
import { article as articleData } from "shared/data/article";
import { notEmpty } from "shared/lib/utils";

import {
  checkArticleIsInSections,
  getArticleType,
} from "scmp-app/components/article/article-render/helpers";
import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import type { articleFooterContent$key } from "scmp-app/queries/__generated__/articleFooterContent.graphql";

import { StyledGenericArticleFooter, StyledMorningStudioArticleFooter } from "./styles";

type Props = {
  articleRecommendation?: {
    allArticleIds: string[];
    articleIds: string[];
    recommendationId: string;
  };
  className?: string;
  divider?: ReactNode;
  reference: articleFooterContent$key;
};
export const ArticleFooter: FunctionComponent<Props> = ({
  articleRecommendation,
  className,
  divider,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment articleFooterContent on Content {
        ... on Article {
          sponsorType
        }
        ...helpersCheckIsStyleArticle
        ...helpersGetArticleTypeArticle
        ...morningStudioArticleFooterContent
        ...helpersCheckArticleIsInSectionsArticle
      }
    `,
    reference,
  );

  const mostPopularQueueName = checkArticleIsInSections(content, [
    sectionData.lifestyle.entityId,
    sectionData.postMagazine.entityId,
    sectionData.peopleCulture.entityId,
  ])
    ? "scmp_trending_section_scmp_trending_section_postmag_last_7_days"
    : undefined;

  if (checkIsStyleArticle(content)) {
    return null;
  }

  switch (getArticleType(content)) {
    case articleData.types.live.entityId:
      return null;
    case articleData.types.studio.entityId:
      return (
        <>
          {divider}
          <StyledMorningStudioArticleFooter className={className} reference={content} />
        </>
      );
    default: {
      const shouldHideMoreOnThisTopic = notEmpty(content.sponsorType);
      if (
        shouldHideMoreOnThisTopic ||
        !notEmpty(articleRecommendation) ||
        articleRecommendation.articleIds.length === 0
      ) {
        return null;
      }

      return (
        <>
          {divider}
          <StyledGenericArticleFooter
            className={className}
            {...articleRecommendation}
            mostPopularQueueName={mostPopularQueueName}
          />
        </>
      );
    }
  }
};

ArticleFooter.displayName = "ArticleFooter";
